/**
 * Código de barras y productos de Open Food Facts (Fase 6, 0.13.0).
 *
 * Todo lo que decide qué se puede usar vive aquí, sin red y sin React, para
 * poder probarlo: validar el código que se escribe o se lee con la cámara, y
 * convertir lo que responde Open Food Facts en un borrador de alimento.
 *
 * Reglas que vienen del resto de la app:
 *
 * - **Nada se inventa.** Un macro que el producto no trae queda en null, no en
 *   cero, y la pantalla dice cuáles faltan (Req. 18.1, 19.4).
 * - **Nada se guarda solo.** Esto arma un borrador; guardarlo es otro paso,
 *   después de revisarlo.
 * - Los valores de Open Food Facts los captura su comunidad a partir de la
 *   etiqueta, pero no son la etiqueta del producto que ella tiene en la mano:
 *   se marcan como `referencia`, nunca como `etiqueta` (Req. 20.7).
 */

import type { CalidadDato, Food, Nutrientes, Porcion } from '@/db/types';

/** Un código válido trae 8, 12, 13 o 14 dígitos (EAN-8, UPC-A, EAN-13, GTIN-14). */
const LARGOS_VALIDOS = new Set([8, 12, 13, 14]);

export type CodigoInvalido = 'vacio' | 'no_digitos' | 'largo' | 'digito_verificador';

export const TEXTO_CODIGO_INVALIDO: Readonly<Record<CodigoInvalido, string>> = {
  vacio: 'Escribe el código de barras del producto.',
  no_digitos: 'El código de barras son solo números.',
  largo: 'Un código de barras tiene 8, 12, 13 o 14 dígitos.',
  digito_verificador: 'Ese código no cuadra: revisa los dígitos, puede faltar o sobrar uno.',
};

export type ResultadoCodigo = { valido: true; codigo: string } | { valido: false; motivo: CodigoInvalido };

/**
 * El dígito verificador de un GTIN: se suman los dígitos alternando pesos 3 y
 * 1 de derecha a izquierda, y el último tiene que completar a la decena. Sirve
 * para cachar un número mal escrito antes de pedirlo a internet.
 */
export function digitoVerificadorCuadra(codigo: string): boolean {
  const digitos = [...codigo].map(Number);
  const verificador = digitos.pop();
  if (verificador === undefined) return false;
  let suma = 0;
  // De derecha a izquierda, el primero pesa 3
  for (const [i, d] of [...digitos].reverse().entries()) suma += d * (i % 2 === 0 ? 3 : 1);
  return (10 - (suma % 10)) % 10 === verificador;
}

/** Valida lo escrito o leído: quita espacios y guiones, que las etiquetas sí traen. */
export function validarCodigo(texto: string): ResultadoCodigo {
  const limpio = texto.replace(/[\s-]/g, '');
  if (limpio === '') return { valido: false, motivo: 'vacio' };
  if (!/^\d+$/.test(limpio)) return { valido: false, motivo: 'no_digitos' };
  if (!LARGOS_VALIDOS.has(limpio.length)) return { valido: false, motivo: 'largo' };
  if (!digitoVerificadorCuadra(limpio)) return { valido: false, motivo: 'digito_verificador' };
  return { valido: true, codigo: limpio };
}

// ---------------------------------------------------------------------------
// Lo que responde Open Food Facts
// ---------------------------------------------------------------------------

/** Solo los campos que se piden; el resto de la respuesta se ignora. */
export interface ProductoOff {
  status?: unknown;
  code?: unknown;
  product?: {
    product_name?: unknown;
    product_name_es?: unknown;
    brands?: unknown;
    quantity?: unknown;
    serving_size?: unknown;
    serving_quantity?: unknown;
    nutriments?: Record<string, unknown>;
  };
}

/** Los campos que se le piden a la API, para no bajar toda la ficha. */
export const CAMPOS_OFF = [
  'code',
  'product_name',
  'product_name_es',
  'brands',
  'quantity',
  'serving_size',
  'serving_quantity',
  'nutriments',
].join(',');

const numero = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v.replace(',', '.')) : v;
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : null;
};

const texto = (v: unknown): string | null => {
  const t = typeof v === 'string' ? v.trim() : '';
  return t === '' ? null : t;
};

/** 1 kcal = 4.184 kJ. Solo se usa si el producto no trae las kcal. */
const KJ_POR_KCAL = 4.184;

export type MacroFaltante = 'proteína' | 'carbohidratos' | 'grasa' | 'fibra';

export interface BorradorDeProducto {
  codigo: string;
  /** "Yogur griego natural · Danone" */
  nombre: string;
  /** Lo que trae por 100 g; lo que el producto no diga queda en null. */
  nutrientes: Nutrientes;
  /** La porción de la etiqueta, si la trae con gramos. */
  porciones: Porcion[];
  /** Lo que dice el empaque del contenido, por ejemplo "500 ml". */
  contenido: string | null;
  /** Macros que el producto no trae: se dicen, no se rellenan con cero. */
  faltantes: MacroFaltante[];
  /** Las kcal venían en kJ y se convirtieron. */
  kcalDesdeKj: boolean;
  calidad: CalidadDato;
}

export type LecturaDeProducto =
  | { tipo: 'listo'; borrador: BorradorDeProducto }
  /** El código no está en Open Food Facts. */
  | { tipo: 'sin_producto' }
  /** Está, pero sin kcal por 100 g: no hay nada que registrar. */
  | { tipo: 'sin_valores'; nombre: string | null }
  /** Está, pero sin nombre: no se puede guardar un alimento sin nombre. */
  | { tipo: 'sin_nombre' };

/**
 * Convierte la respuesta en un borrador. Devuelve por qué no se puede cuando
 * falta lo mínimo: el nombre y las kcal por 100 g.
 */
export function leerProducto(respuesta: ProductoOff, codigo: string): LecturaDeProducto {
  const p = respuesta.product;
  if (p === undefined || respuesta.status === 0) return { tipo: 'sin_producto' };

  const marca = texto(p.brands)?.split(',')[0]?.trim() ?? null;
  const base = texto(p.product_name_es) ?? texto(p.product_name);
  const nutrientesCrudos = p.nutriments ?? {};

  const kcalDirectas = numero(nutrientesCrudos['energy-kcal_100g']);
  const kj = numero(nutrientesCrudos['energy-kj_100g']) ?? numero(nutrientesCrudos['energy_100g']);
  const kcal = kcalDirectas ?? (kj === null ? null : Math.round((kj / KJ_POR_KCAL) * 10) / 10);
  if (kcal === null) return { tipo: 'sin_valores', nombre: base };
  if (base === null) return { tipo: 'sin_nombre' };

  const proteinaG = numero(nutrientesCrudos['proteins_100g']);
  const carbohidratosG = numero(nutrientesCrudos['carbohydrates_100g']);
  const grasaG = numero(nutrientesCrudos['fat_100g']);
  const fibraG = numero(nutrientesCrudos['fiber_100g']);

  const faltantes: MacroFaltante[] = [
    proteinaG === null ? ('proteína' as const) : null,
    carbohidratosG === null ? ('carbohidratos' as const) : null,
    grasaG === null ? ('grasa' as const) : null,
    fibraG === null ? ('fibra' as const) : null,
  ].filter((m) => m !== null);

  // La porción de la etiqueta solo sirve si dice cuántos gramos son
  const gramosPorcion = numero(p.serving_quantity);
  const nombrePorcion = texto(p.serving_size);
  const porciones: Porcion[] =
    gramosPorcion !== null && gramosPorcion > 0
      ? [{ nombre: nombrePorcion ?? 'porción', gramos: gramosPorcion }]
      : [];

  return {
    tipo: 'listo',
    borrador: {
      codigo,
      nombre: marca === null ? base : `${base} · ${marca}`,
      nutrientes: { kcal, proteinaG, carbohidratosG, grasaG, fibraG },
      porciones,
      contenido: texto(p.quantity),
      faltantes,
      kcalDesdeKj: kcalDirectas === null,
      calidad: 'referencia',
    },
  };
}

/** "Faltan proteína y fibra: quedan en blanco, no en cero." */
export function textoDeFaltantes(faltantes: readonly MacroFaltante[]): string | null {
  if (faltantes.length === 0) return null;
  const lista =
    faltantes.length === 1
      ? faltantes[0]
      : `${faltantes.slice(0, -1).join(', ')} y ${faltantes[faltantes.length - 1]}`;
  return `Open Food Facts no trae ${lista} de este producto: ${
    faltantes.length === 1 ? 'queda en blanco' : 'quedan en blanco'
  }, no en cero. Puedes completarlo con la etiqueta antes de guardar.`;
}

/** La nota que queda guardada, para saber de dónde salieron los valores (Req. 20.7). */
export function notaDelProducto(b: BorradorDeProducto, hoy: string): string {
  return [
    `Open Food Facts, código ${b.codigo}, consultado el ${hoy}.`,
    b.contenido === null ? null : `Contenido del empaque: ${b.contenido}.`,
    b.kcalDesdeKj ? 'Las kcal se calcularon desde los kJ del producto.' : null,
    b.faltantes.length === 0 ? null : `Sin dato de ${b.faltantes.join(', ')}.`,
    'Los valores los captura la comunidad de Open Food Facts: no son la etiqueta de tu producto.',
  ]
    .filter((x) => x !== null)
    .join(' ');
}

/** El borrador como alimento propio, listo para revisar y guardar. */
export function alimentoDesdeProducto(b: BorradorDeProducto, id: string, hoy: string, ahora: string): Food {
  return {
    id,
    nombre: b.nombre,
    referencia: { tipo: 'por100g' },
    nutrientes: b.nutrientes,
    porciones: b.porciones,
    rompeAyuno: true,
    favorito: false,
    archivado: false,
    origenBaseId: null,
    estado: null,
    calidad: b.calidad,
    nota: notaDelProducto(b, hoy),
    creadoEn: ahora,
    actualizadoEn: ahora,
  };
}
